"use client";

import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/translationContext";
import { useState, useEffect } from "react";
import SheetModal from "@/components/ui/sheet-modal";
import { cn } from "@/lib/utils";
import { Input } from "@/components/views/add-place/Input";
import toast from "react-hot-toast";
import { PhoneIcon, AtSymbolIcon, MapPinIcon, EnvelopeIcon, KeyIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import isPhoneValid from "@/lib/is-phone-valid";
import { MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from "@/auth";
import { ROUTES } from "@/@core/constants/routes";
import { Checkbox } from "@/components/ui/checkbox";
import { useSession } from "next-auth/react";
import { getLatestContestDataAction } from "@/actions/comment/get-latest-contest-data";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

interface Props {
  onClose: VoidFunction;
  isOpen: boolean;
  onSubmit?: (data: ContestFormData) => void;
  title?: string;
  description?: string;
}

/**
 * Built from the caller's `t`: at module scope there is no translator, which
 * is what kept these messages out of Weblate.
 */
type Translate = (key: string, options?: Record<string, any>) => string;

const contestSchema = (t: Translate) =>
  z.object({
    phone: z
      .string()
      .min(1, t("contest-phone-required-warning"))
      .refine((val) => isPhoneValid(`+90${val}`), {
        message: t("contest-phone-invalid-warning"),
      }),
    instagram: z
      .string()
      .min(1, t("contest-instagram-username-required-warning"))
      .min(3, t("contest-instagram-username-min-length-warning"))
      .max(30, t("contest-instagram-username-max-length-warning"))
      .regex(/^[a-zA-Z0-9._]+$/, t("contest-instagram-username-invalid-warning")),
    address: z
      .string()
      .min(1, t("contest-address-required-warning"))
      .min(10, t("contest-address-min-length-warning"))
      .max(200, t("contest-address-max-length-warning")),
    email: z
      .string()
      .min(1, t("contest-email-required-warning"))
      .max(100, t("contest-email-max-length-warning"))
      .email(t("contest-email-invalid-warning")),
    password: z.string().optional(),
    termsOfUse: z.boolean().optional(),
  });

export type ContestFormData = z.infer<ReturnType<typeof contestSchema>>;

const CONTEST_STORAGE_KEY = "contest-form-data";

type StoredContestData = Pick<ContestFormData, "phone" | "instagram" | "address" | "email">;

function readStoredContestData(): StoredContestData | null {
  try {
    const raw = localStorage.getItem(CONTEST_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeContestData(data: StoredContestData) {
  try {
    localStorage.setItem(CONTEST_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage unavailable (private mode, quota) — prefill just won't work
  }
}

export default function ContestDrawer(props: Props) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { status: sessionStatus, data: session } = useSession();
  const isAuthenticated = sessionStatus === "authenticated";

  const form = useForm<ContestFormData>({
    resolver: zodResolver(contestSchema(t)),
    defaultValues: {
      phone: "",
      instagram: "",
      address: "",
      email: "",
      password: "",
      termsOfUse: true
    },
  });

  // Auto-fill email for authenticated users
  useEffect(() => {
    if (isAuthenticated && session?.user?.email) {
      form.setValue("email", session.user.email);
    }
  }, [isAuthenticated, session, form]);

  // Auto-fill from the last submission stored on this device (works for guests too)
  useEffect(() => {
    if (!props.isOpen) return;

    const stored = readStoredContestData();
    if (!stored) return;

    (["phone", "instagram", "address", "email"] as const).forEach((field) => {
      if (stored[field] && !form.getValues(field)) {
        form.setValue(field, stored[field]);
      }
    });
  }, [props.isOpen, form]);

  // Auto-fill phone, instagram, address from user's most recent contest entry
  useEffect(() => {
    if (!isAuthenticated || !props.isOpen) return;

    let cancelled = false;
    (async () => {
      const result = await getLatestContestDataAction();
      if (cancelled || !result.success || !result.data) return;

      const { phone, instagram_username, address } = result.data;

      if (phone && !form.getValues("phone")) {
        const digits = phone.replace(/\D/g, "");
        const normalized = digits.startsWith("90") ? digits.slice(2) : digits;
        form.setValue("phone", normalized.slice(0, 10));
      }
      if (instagram_username && !form.getValues("instagram")) {
        form.setValue("instagram", instagram_username);
      }
      if (address && !form.getValues("address")) {
        form.setValue("address", address);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, props.isOpen, form]);

  const handleSubmit = async (data: ContestFormData) => {
    // Add +90 prefix to phone number
    const formattedData = {
      ...data,
      phone: `+90${data.phone}`,
    };

    // Validate password and terms for unauthenticated users
    if (!isAuthenticated) {
      if (!data.password || data.password.length < MIN_PASSWORD_LENGTH) {
        form.setError("password", {
          type: "manual",
          message: t("contest-password-min-length-warning")
        });
        return;
      }
      if (!data.termsOfUse) {
        form.setError("termsOfUse", {
          type: "manual",
          message: t("form-terms-must-accept-warning")
        });
        return;
      }
    }

    storeContestData({
      phone: data.phone,
      instagram: data.instagram,
      address: data.address,
      email: data.email,
    });

    setIsLoading(true);

    try {
      if (props.onSubmit) {
        await props.onSubmit(formattedData);
      } else {
        toast.success(t("contest-submit-success"));
        form.reset();
        props.onClose();
      }
    } catch (error) {
      toast.error(t("contest-generic-error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    props.onClose();
  };

  return (
    <SheetModal isOpen={props.isOpen} onClose={handleClose}>
      <div className="py-6 px-2">
        <div className="mb-6">
          <h2 className="text-lg font-bold text-assistant mb-2">
            {props.title || t("contest-form-title")}
          </h2>
          <p className="text-sm text-gray-600">
            {props.description || (isAuthenticated
              ? t("contest-form-description")
              : t("contest-comment-form-description")
            )}
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        field.onChange(value);
                      }}
                      icon={<PhoneIcon className="size-5" />}
                      placeholder={t("contest-phone-placeholder")}
                      id="phone"
                      type="tel"
                      prefix="+90"
                      inputMode="numeric"
                      maxLength={10}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="instagram"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      icon={<AtSymbolIcon className="size-5" />}
                      placeholder={t("contest-instagram-username-placeholder")}
                      id="instagram"
                      maxLength={30}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      icon={<MapPinIcon className="size-5" />}
                      placeholder="Adres"
                      id="address"
                      maxLength={200}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      icon={<EnvelopeIcon className="size-5" />}
                      placeholder="Email"
                      id="email"
                      type="email"
                      maxLength={100}
                      readOnly={isAuthenticated}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isAuthenticated && (
              <>
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          icon={<KeyIcon className="size-5" />}
                          placeholder={t("user-login-password-placeholder")}
                          id="password"
                          type="password"
                          minLength={MIN_PASSWORD_LENGTH}
                          maxLength={MAX_PASSWORD_LENGTH}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="termsOfUse"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 text-primary text-sm font-bold">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="rounded-lg border-assistant w-6 h-6"
                          />
                        </FormControl>
                        <label htmlFor="termsOfUse" className="cursor-pointer">
                          <a
                            target="_blank"
                            href={ROUTES.LEGAL.TERMS}
                            className="underline"
                            rel="noreferrer"
                          >
                            Kullanım Koşulları
                          </a>{" "}
                          ve{" "}
                          <a
                            target="_blank"
                            href={ROUTES.LEGAL.WARNING}
                            className="underline"
                            rel="noreferrer"
                          >
                            Gizlilik Sözleşmesi
                          </a>{" "}
                          okudum, kabul ediyorum
                        </label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              variant="accent"
              className={cn(
                `flex font-bold text-sm mt-4`,
                isLoading ? "cursor-not-allowed" : "",
              )}
            >
              {isLoading ? t("blog-detail-comment-submit-loading") : "Gönder"}
            </Button>
          </form>
        </Form>
      </div>
    </SheetModal>
  );
}